import React, { useState, useCallback } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import AvailabilityGrid from '../components/AvailabilityGrid';
import HongKongDistrictSelector from '../components/HongKongDistrictSelector';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { tutorPlatformDb, tutorPlatformStorage, ID, Permission, Role } from '../services/appwrite';
import {
  User,
  Clock,
  BookOpen,
  Upload,
  CheckCircle,
  AlertCircle,
  School,
  Users,
  Book,
  DollarSign,
  FileText,
  Image,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

const TEACHING_STYLES = [
  'Structured', 'Flexible', 'Practical', 'Fun / Games / Activities',
  'Story/Song/Video-based', 'Reading & Writing focus', 'Speaking & Pronunciation focus',
  'Exam-focused', 'Business-focused', 'Kids-friendly', 'Tech-assisted lessons',
];

const AGE_GROUP_OPTIONS = [
  { value: 'playgroup', label: 'Playgroup (0–3 years)' },
  { value: 'primary', label: 'Primary school (4–12 years)' },
  { value: 'secondary', label: 'Secondary school (13–17 years)' },
  { value: 'adults', label: 'Adults (18+ years)' },
];

const STUDENT_LANGUAGES = [
  'English-speaking students',
  'Cantonese-speaking students',
  'Mandarin-speaking students',
];

const CURRICULUMS = [
  { category: 'Hong Kong', options: [
    { value: 'hkdse', label: 'HKDSE Chinese Language' },
  ]},
  { category: 'UK: GCSE and IGCSE', options: [
    { value: 'cambridge_igcse_0547', label: 'Cambridge IGCSE Mandarin Chinese 0547 Foreign Language' },
    { value: 'cambridge_igcse_0523', label: 'Cambridge IGCSE Chinese 0523 Second Language' },
    { value: 'cambridge_igcse_0509', label: 'Cambridge IGCSE Chinese 0509 First Language' },
    { value: 'edexcel_gcse', label: 'Pearson Edexcel GCSE Chinese 1CN0' },
    { value: 'edexcel_igcse', label: 'Pearson Edexcel International GCSE Chinese 9–1' },
    { value: 'aqa_gcse', label: 'AQA GCSE Chinese Mandarin 8673' },
  ]},
  { category: 'UK: A level', options: [
    { value: 'edexcel_alevel', label: 'Pearson Edexcel A level Chinese 9CN0' },
    { value: 'cambridge_alevel', label: 'Cambridge International AS and A Level Chinese Language and Literature 9868' },
  ]},
  { category: 'US: Advanced Placement', options: [
    { value: 'ap_chinese', label: 'AP Chinese Language and Culture' },
  ]},
  { category: 'Proficiency', options: [
    { value: 'hsk1', label: 'HSK 1' },
    { value: 'hsk2', label: 'HSK 2' },
    { value: 'hsk3', label: 'HSK 3' },
    { value: 'hsk4', label: 'HSK 4' },
    { value: 'hsk5', label: 'HSK 5' },
    { value: 'hsk6', label: 'HSK 6' },
    { value: 'hskk_beginner', label: 'HSKK Beginner' },
    { value: 'hskk_intermediate', label: 'HSKK Intermediate' },
    { value: 'hskk_advanced', label: 'HSKK Advanced' },
  ]},
];

// ---------------------------------------------------------------------------
// Small reusable pieces
// ---------------------------------------------------------------------------

function CheckboxOption({ label, checked, onChange }) {
  return (
    <label
      className="flex items-center p-3 rounded-lg border cursor-pointer transition-all hover:border-gray-600"
      style={{
        borderColor: checked ? '#7e55f6' : '#374151',
        backgroundColor: checked ? 'rgba(126,85,246,0.12)' : 'transparent',
      }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <div
        className="w-5 h-5 rounded border-2 mr-3 flex items-center justify-center shrink-0"
        style={{
          borderColor: checked ? '#7e55f6' : '#4b5563',
          backgroundColor: checked ? '#7e55f6' : 'transparent',
        }}
      >
        {checked && (
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
      <span className="text-sm font-medium text-gray-200">{label}</span>
    </label>
  );
}

function RadioOption({ label, checked, onChange }) {
  return (
    <label className="flex items-center cursor-pointer">
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4"
        style={{ accentColor: '#7e55f6' }}
      />
      <span className="ml-2 text-gray-200">{label}</span>
    </label>
  );
}

function SectionHeader({ title, icon, isActive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-6 py-4 border-b transition-colors text-left ${
        isActive ? 'border-purple-800' : 'border-gray-700 hover:bg-gray-800'
      }`}
      style={isActive ? { backgroundColor: 'rgba(126,85,246,0.1)' } : { backgroundColor: '#1f2937' }}
    >
      <h3 className="font-semibold text-base flex items-center text-white">
        {icon}
        <span className="ml-2">{title}</span>
      </h3>
      {isActive ? (
        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
      ) : (
        <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
      )}
    </button>
  );
}

function FieldLabel({ children, required }) {
  return (
    <label className="block text-sm font-medium text-gray-300 mb-1.5">
      {children}
      {required && <span className="text-purple-400 ml-1">*</span>}
    </label>
  );
}

function SubHeading({ children }) {
  return <h4 className="text-sm font-semibold text-purple-400 mb-2">{children}</h4>;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-700 bg-gray-800 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors';

const textareaCls =
  'w-full px-3 py-2 border border-gray-700 bg-gray-800 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none';

// ---------------------------------------------------------------------------
// ApplyPage
// ---------------------------------------------------------------------------

const ApplyPage = () => {
  useDocumentTitle('Apply to Be a Tutor');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '+852 ',
    yearsExperience: '',
    tutorBio: '',
    teachingStyles: [],
    otherTeachingStyle: '',
    notes: '',
    ageGroups: [],
    studentLanguages: [],
    classFormats: [],
    availability: {},
    availabilityNotes: '',
    curriculums: [],
    otherCurriculum: '',
    specialisms: [],
    schoolInterviewDetails: '',
    jobInterviewDetails: '',
    businessMandarinDetails: '',
    businessCantoneseDetails: '',
    textbooks: '',
    digitalTools: '',
    materialSupport: 'flexible',
    noTextbook: 'yes',
    customMaterials: 'yes',
    websiteConsent: '',
    hourlyRateOnline: '',
    hourlyRateInPerson: '',
    locationPreference: 'both',
    baseDistricts: [],
    profilePhoto: null,
    supplementaryPhotos: [null, null, null, null],
    resume: null,
    sampleLesson: null,
    curriculumExperience: {},
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState({ profilePhoto: '', resume: '', sampleLesson: '' });
  const [supplementaryPhotoNames, setSupplementaryPhotoNames] = useState(['', '', '', '']);
  const [activeSection, setActiveSection] = useState('personal');
  const [bioCount, setBioCount] = useState(0);
  const [notesCount, setNotesCount] = useState(0);

  // -- handlers ---------------------------------------------------------------

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phoneNumber') {
      if (!value.startsWith('+')) {
        setFormData((p) => ({ ...p, phoneNumber: '+852 ' }));
        return;
      }
      if (value === '+' || value === '+8' || value === '+85') {
        setFormData((p) => ({ ...p, phoneNumber: '+852 ' }));
        return;
      }
    }

    setFormData((p) => ({ ...p, [name]: value }));

    if (name === 'tutorBio') setBioCount(value.length);
    if (name === 'notes') setNotesCount(value.length);
  };

  const handleCheckbox = (field, value) => {
    setFormData((p) => {
      const current = p[field];
      return {
        ...p,
        [field]: current.includes(value) ? current.filter((i) => i !== value) : [...current, value],
      };
    });
  };

  const handleRadio = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    if (field === 'locationPreference') document.activeElement?.blur();
  };

  const handleAvailabilityChange = (avail) => {
    setFormData((p) => ({ ...p, availability: avail }));
  };

  const handleDistrictsChange = useCallback((districts) => {
    setFormData((p) => ({ ...p, baseDistricts: districts }));
  }, []);

  const handleFileChange = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = field === 'sampleLesson' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File size must be less than ${field === 'sampleLesson' ? '10MB' : '5MB'}`);
      return;
    }

    const allowedByField = {
      profilePhoto: ['.jpg', '.jpeg', '.png', '.webp'],
      resume: ['.pdf', '.doc', '.docx'],
      sampleLesson: ['.pdf', '.doc', '.docx', '.ppt', '.pptx'],
    };
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedByField[field].includes(ext)) {
      setError(`Invalid file type. Allowed: ${allowedByField[field].join(', ')}`);
      return;
    }

    setFormData((p) => ({ ...p, [field]: file }));
    setFileName((p) => ({ ...p, [field]: file.name }));
    setError(null);
  };

  const handleDrop = (e, field, index) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (field === 'supplementaryPhoto') {
      handleSupplementaryPhotoChange({ target: { files: [file] } }, index);
    } else {
      handleFileChange({ target: { files: [file] } }, field);
    }
  };

  const handleSupplementaryPhotoChange = (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!['.jpg', '.jpeg', '.png', '.webp'].includes(ext)) {
      setError('Invalid file type. Allowed: .jpg, .jpeg, .png, .webp');
      return;
    }
    setFormData((p) => {
      const updated = [...p.supplementaryPhotos];
      updated[index] = file;
      return { ...p, supplementaryPhotos: updated };
    });
    setSupplementaryPhotoNames((p) => {
      const updated = [...p];
      updated[index] = file.name;
      return updated;
    });
    setError(null);
  };

  const handleCurriculumExperienceChange = (value, text) => {
    setFormData((p) => ({
      ...p,
      curriculumExperience: { ...p.curriculumExperience, [value]: text },
    }));
  };

  const toggleSection = (section) => {
    setActiveSection((p) => {
      const next = p === section ? '' : section;
      if (next) {
        setTimeout(() => {
          document.getElementById(`section-${next}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
      }
      return next;
    });
  };

  const handleErrorLinkClick = (e, sectionId) => {
    e.preventDefault();
    setActiveSection(sectionId);
    setTimeout(() => {
      document.getElementById(`section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // -- validation -------------------------------------------------------------

  const validate = () => {
    if (!formData.firstName.trim()) return { message: 'First name is required', section: 'personal' };
    if (!formData.lastName.trim()) return { message: 'Last name is required', section: 'personal' };
    if (!formData.email.trim()) return { message: 'Email is required', section: 'personal' };
    if (!/\S+@\S+\.\S+/.test(formData.email)) return { message: 'Please enter a valid email', section: 'personal' };
    const normalizedPhone = formData.phoneNumber.replace(/[\s\-()]/g, '');
    if (!normalizedPhone.startsWith('+')) return { message: 'Phone must start with a country code e.g. +852', section: 'personal' };
    if (normalizedPhone.length < 8 || normalizedPhone.length > 16) return { message: 'Phone number must be 8–15 digits including country code', section: 'personal' };
    if (!/^\+\d+$/.test(normalizedPhone)) return { message: 'Phone number can only contain digits after the + sign', section: 'personal' };
    if (!formData.profilePhoto) return { message: 'Profile photo is required', section: 'photo' };
    if (!formData.resume) return { message: 'Resumé is required', section: 'photo' };
    if (!formData.yearsExperience) return { message: 'Years of experience is required', section: 'teaching' };
    if (!formData.tutorBio.trim()) return { message: 'Tutor bio is required', section: 'teaching' };
    if (formData.tutorBio.length > 300) return { message: 'Tutor bio must be 300 characters or less', section: 'teaching' };
    if (formData.teachingStyles.length === 0) return { message: 'Please select at least one teaching style', section: 'teaching' };
    if (formData.ageGroups.length === 0) return { message: 'Please select at least one age group', section: 'students' };
    if (formData.studentLanguages.length === 0) return { message: 'Please select at least one student language background', section: 'students' };
    if (formData.classFormats.length === 0) return { message: 'Please select at least one class format', section: 'students' };
    if (Object.values(formData.availability).filter(Boolean).length === 0) return { message: 'Please select at least one availability time slot', section: 'availability' };
    if (formData.locationPreference !== 'online_only' && formData.baseDistricts.length === 0) return { message: 'Please select at least one base district for in-person lessons', section: 'compensation' };
    if (!formData.hourlyRateOnline.trim() || isNaN(Number(formData.hourlyRateOnline))) return { message: 'Please enter your proposed hourly rate for online lessons', section: 'compensation' };
    if (formData.locationPreference !== 'online_only' && (!formData.hourlyRateInPerson.trim() || isNaN(Number(formData.hourlyRateInPerson)))) return { message: 'Please enter your proposed hourly rate for in-person lessons', section: 'compensation' };
    if (!formData.websiteConsent) return { message: 'Please indicate whether you consent to your profile being shown', section: 'consent' };
    return null;
  };

  // -- submit -----------------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const photoFile = await tutorPlatformStorage.createFile(
        'tutor-files', ID.unique(), formData.profilePhoto, [Permission.read(Role.any())]
      );

      const resumeFile = await tutorPlatformStorage.createFile(
        'tutor-files', ID.unique(), formData.resume, [Permission.read(Role.any())]
      );

      let sampleFileId = null;
      if (formData.sampleLesson) {
        const sampleFile = await tutorPlatformStorage.createFile(
          'tutor-files', ID.unique(), formData.sampleLesson, [Permission.read(Role.any())]
        );
        sampleFileId = sampleFile.$id;
      }

      const supplementaryPhotoIds = [];
      for (const photo of formData.supplementaryPhotos) {
        if (photo) {
          const uploaded = await tutorPlatformStorage.createFile(
            'tutor-files', ID.unique(), photo, [Permission.read(Role.any())]
          );
          supplementaryPhotoIds.push(uploaded.$id);
        }
      }

      await tutorPlatformDb.createDocument('platform', 'tutors', ID.unique(), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        yearsExperience: Number(formData.yearsExperience) || 0,
        tutorBio: formData.tutorBio,
        teachingStyles: formData.teachingStyles,
        otherTeachingStyle: formData.otherTeachingStyle,
        notes: formData.notes,
        ageGroups: formData.ageGroups,
        studentLanguages: formData.studentLanguages,
        classFormats: formData.classFormats,
        availability: Object.keys(formData.availability).filter(k => formData.availability[k]),
        availabilityNotes: formData.availabilityNotes,
        curriculums: formData.curriculums,
        curriculumExperience: JSON.stringify(formData.curriculumExperience),
        otherCurriculum: formData.otherCurriculum,
        specialisms: formData.specialisms,
        schoolInterviewDetails: formData.schoolInterviewDetails,
        jobInterviewDetails: formData.jobInterviewDetails,
        businessMandarinDetails: formData.businessMandarinDetails,
        businessCantoneseDetails: formData.businessCantoneseDetails,
        textbooks: formData.textbooks,
        digitalTools: formData.digitalTools,
        materialSupport: formData.materialSupport,
        noTextbook: formData.noTextbook === 'yes',
        customMaterials: formData.customMaterials === 'yes',
        hourlyRateOnline: Number(formData.hourlyRateOnline) || 0,
        hourlyRateInPerson: Number(formData.hourlyRateInPerson) || 0,
        locationPreference: formData.locationPreference,
        baseDistricts: formData.baseDistricts,
        websiteConsent: formData.websiteConsent === 'yes',
        profilePhotoId: photoFile.$id,
        resumeId: resumeFile.$id,
        sampleLessonId: sampleFileId || '',
        supplementaryPhotoIds: supplementaryPhotoIds,
        status: 'pending',
      });

      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Failed to submit application. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // -- success state ----------------------------------------------------------

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6 flex justify-center">
            <CheckCircle size={64} className="text-green-400" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Application Submitted!</h2>
          <div className="w-16 h-1 mx-auto mb-6" style={{ background: 'linear-gradient(to right, #7e55f6, #6366f1)' }} />
          <p className="text-gray-400 mb-4">
            Thank you for your interest in joining our team! We've received your application.
          </p>
          <p className="text-gray-500 mb-8">
            We'll review your application and get back to you within 3–5 business days.
          </p>
          <p className="text-gray-500 mb-4">Questions about your application?</p>
          <a
            href="https://wa.me/85251776613"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Contact Us on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  // -- main render ------------------------------------------------------------

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      <main className="max-w-5xl mx-auto py-12 px-4 sm:px-6">
        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold tracking-tight text-white mb-4">
            Become a Tutor
          </h1>
          <div className="w-16 h-1 mx-auto mb-5" style={{ background: 'linear-gradient(to right, #7e55f6, #6366f1)' }} />
          <p className="text-gray-400 max-w-xl mx-auto">
            We welcome reliable, friendly Mandarin and Cantonese tutors. Shortlisted applicants will
            be contacted for a quick call and a short demo lesson. If it's a good fit, your profile
            will be featured on our website so people can hire you.
          </p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-950 border border-red-800 rounded-xl flex items-start gap-3">
            <AlertCircle size={18} className="text-red-400 mt-0.5 shrink-0" />
            <div className="text-sm text-red-400">
              {typeof error === 'string' ? (
                <p>{error}</p>
              ) : (
                <p>
                  {error.message}{' '}
                  <a
                    href={`#section-${error.section}`}
                    onClick={(e) => handleErrorLinkClick(e, error.section)}
                    className="underline font-medium hover:text-red-300"
                  >
                    Go to section →
                  </a>
                </p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ── Personal Information ──────────────────────────────────────────────── */}
          <div id="section-personal" className="bg-gray-900 rounded-xl border border-gray-800 shadow-sm overflow-hidden">
            <SectionHeader
              title="Personal Information"
              icon={<User size={18} />}
              isActive={activeSection === 'personal'}
              onClick={() => toggleSection('personal')}
            />
            {activeSection === 'personal' && (
              <div className="p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>First Name</FieldLabel>
                    <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Jane"
                      className={inputCls}
                      required
                    />
                  </div>
                  <div>
                    <FieldLabel required>Last Name</FieldLabel>
                    <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Ho"
                      className={inputCls}
                      required
                    />
                  </div>
                </div>
                <div>
                  <FieldLabel required>Email</FieldLabel>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jane@example.com"
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <FieldLabel required>WhatsApp Phone</FieldLabel>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+852 1234 4321"
                    className={inputCls}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Include country code, e.g. +852 for Hong Kong.</p>
                </div>
              </div>
            )}
          </div>

          {/* ── Profile Photo & Resumé ──────────────────────────────────────────── */}
          <div id="section-photo" className="bg-gray-900 rounded-xl border border-gray-800 shadow-sm overflow-hidden">
            <SectionHeader
              title="Profile Photo and Resumé"
              icon={<Image size={18} />}
              isActive={activeSection === 'photo'}
              onClick={() => toggleSection('photo')}
            />
            {activeSection === 'photo' && (
              <div className="p-6 space-y-6">
                {/* Primary headshot */}
                <div>
                  <FieldLabel required>Primary Headshot</FieldLabel>
                  <label
                    htmlFor="upload-photo"
                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500 transition-colors"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, 'profilePhoto')}
                  >
                    <input
                      id="upload-photo"
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={(e) => handleFileChange(e, 'profilePhoto')}
                      className="sr-only"
                    />
                    <Upload size={40} className="text-gray-600 mb-3" />
                    {fileName.profilePhoto ? (
                      <>
                        <p className="text-green-400 font-medium flex items-center gap-1.5 text-sm">
                          <CheckCircle size={15} /> {fileName.profilePhoto}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">Click to change</p>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-300 text-sm">
                          <span className="text-purple-400 font-medium">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-gray-500 text-xs mt-1">JPG, PNG, WEBP (max 5 MB)</p>
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    A clear, friendly photo helps build trust with parents and clients. Any photo
                    you're comfortable sharing is fine for now — we can help with a professional
                    headshot later.
                  </p>
                </div>

                {/* Supplementary photos */}
                <div>
                  <FieldLabel>Supplementary Photos (optional, up to 4)</FieldLabel>
                  <p className="text-xs text-gray-500 mb-3">
                    Additional photos showcasing you teaching, your workspace, or other aspects of your tutoring. JPG, PNG, WEBP (max 5 MB each).
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[0, 1, 2, 3].map((i) => (
                      <label
                        key={i}
                        htmlFor={`upload-supplementary-${i}`}
                        className="flex flex-col items-center justify-center border-2 border-dashed border-gray-700 rounded-xl p-5 text-center cursor-pointer hover:border-purple-500 transition-colors"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => handleDrop(e, 'supplementaryPhoto', i)}
                      >
                        <input
                          id={`upload-supplementary-${i}`}
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) => handleSupplementaryPhotoChange(e, i)}
                          className="sr-only"
                        />
                        <Image size={28} className="text-gray-600 mb-2" />
                        {supplementaryPhotoNames[i] ? (
                          <>
                            <p className="text-green-400 font-medium flex items-center gap-1 text-xs">
                              <CheckCircle size={13} /> {supplementaryPhotoNames[i]}
                            </p>
                            <p className="text-gray-500 text-xs mt-0.5">Click to change</p>
                          </>
                        ) : (
                          <p className="text-gray-400 text-xs">
                            <span className="text-purple-400 font-medium">Photo {i + 1}</span>
                          </p>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Resume upload */}
                <div>
                  <FieldLabel required>Resumé</FieldLabel>
                  <label
                    htmlFor="upload-resume"
                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500 transition-colors"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, 'resume')}
                  >
                    <input
                      id="upload-resume"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'resume')}
                      className="sr-only"
                    />
                    <FileText size={40} className="text-gray-600 mb-3" />
                    {fileName.resume ? (
                      <>
                        <p className="text-green-400 font-medium flex items-center gap-1.5 text-sm">
                          <CheckCircle size={15} /> {fileName.resume}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">Click to change</p>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-300 text-sm">
                          <span className="text-purple-400 font-medium">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-gray-500 text-xs mt-1">PDF, DOC, DOCX (max 5 MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* ── Teaching Profile ────────────────────────────────────────────────────────── */}
          <div id="section-teaching" className="bg-gray-900 rounded-xl border border-gray-800 shadow-sm overflow-hidden">
            <SectionHeader
              title="Teaching Profile"
              icon={<School size={18} />}
              isActive={activeSection === 'teaching'}
              onClick={() => toggleSection('teaching')}
            />
            {activeSection === 'teaching' && (
              <div className="p-6 space-y-6">
                <div>
                  <FieldLabel required>Years of Experience</FieldLabel>
                  <input
                    type="number"
                    name="yearsExperience"
                    value={formData.yearsExperience}
                    onChange={handleChange}
                    min="0"
                    placeholder="5"
                    className={`${inputCls} w-32`}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Whole years only.</p>
                </div>

                <div>
                  <FieldLabel required>Short Tutor Bio (public, max 300 characters)</FieldLabel>
                  <textarea
                    name="tutorBio"
                    value={formData.tutorBio}
                    onChange={handleChange}
                    placeholder="1–2 sentences focused on student outcomes. Example: '8 years helping GCSE, IB, and AP students improve speaking and grades with clear targets and practical practice.'"
                    rows={4}
                    maxLength={300}
                    className={`${textareaCls} ${bioCount > 300 ? 'border-red-500' : ''}`}
                    required
                  />
                  <p className={`text-right text-xs mt-1 ${bioCount > 300 ? 'text-red-400' : 'text-gray-500'}`}>
                    {bioCount}/300
                  </p>
                </div>

                <div>
                  <FieldLabel required>Teaching Style (select all that apply)</FieldLabel>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {TEACHING_STYLES.map((s) => (
                      <CheckboxOption
                        key={s}
                        label={s}
                        checked={formData.teachingStyles.includes(s)}
                        onChange={() => handleCheckbox('teachingStyles', s)}
                      />
                    ))}
                  </div>
                  <div className="mt-3">
                    <FieldLabel>Other (specify)</FieldLabel>
                    <input
                      type="text"
                      name="otherTeachingStyle"
                      value={formData.otherTeachingStyle}
                      onChange={handleChange}
                      placeholder="Any other teaching styles"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <FieldLabel>Optional Notes (internal, max 300 characters)</FieldLabel>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Anything we should know that's not in your CV."
                    rows={3}
                    maxLength={300}
                    className={textareaCls}
                  />
                  <p className="text-right text-xs text-gray-500 mt-1">{notesCount}/300</p>
                </div>
              </div>
            )}
          </div>

          {/* ── Students ──────────────────────────────────────────────────────────────────────── */}
          <div id="section-students" className="bg-gray-900 rounded-xl border border-gray-800 shadow-sm overflow-hidden">
            <SectionHeader
              title="Students You Can Teach"
              icon={<Users size={18} />}
              isActive={activeSection === 'students'}
              onClick={() => toggleSection('students')}
            />
            {activeSection === 'students' && (
              <div className="p-6 space-y-6">
                <div>
                  <FieldLabel required>Age Groups (select all that apply)</FieldLabel>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {AGE_GROUP_OPTIONS.map((opt) => (
                      <CheckboxOption
                        key={opt.value}
                        label={opt.label}
                        checked={formData.ageGroups.includes(opt.value)}
                        onChange={() => handleCheckbox('ageGroups', opt.value)}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <FieldLabel required>Class Formats (select all that apply)</FieldLabel>
                  <div className="space-y-3">
                    <div>
                      <SubHeading>In-Person</SubHeading>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {['One-on-one', 'Group classes'].map((f) => (
                          <CheckboxOption
                            key={f}
                            label={f}
                            checked={formData.classFormats.includes(f)}
                            onChange={() => handleCheckbox('classFormats', f)}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <SubHeading>Online</SubHeading>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {['Online 1-1', 'Online group'].map((f) => (
                          <CheckboxOption
                            key={f}
                            label={f === 'Online 1-1' ? 'One-on-one' : 'Group classes'}
                            checked={formData.classFormats.includes(f)}
                            onChange={() => handleCheckbox('classFormats', f)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <FieldLabel required>Student Language Backgrounds (select all that apply)</FieldLabel>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {STUDENT_LANGUAGES.map((l) => (
                      <CheckboxOption
                        key={l}
                        label={l}
                        checked={formData.studentLanguages.includes(l)}
                        onChange={() => handleCheckbox('studentLanguages', l)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Availability ─────────────────────────────────────────────────────────────────── */}
          <div id="section-availability" className="bg-gray-900 rounded-xl border border-gray-800 shadow-sm overflow-hidden">
            <SectionHeader
              title="Availability"
              icon={<Clock size={18} />}
              isActive={activeSection === 'availability'}
              onClick={() => toggleSection('availability')}
            />
            {activeSection === 'availability' && (
              <div className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <FieldLabel required>Select your available times</FieldLabel>
                    <p className="text-sm text-gray-500">
                      Click a slot to mark availability. Click and drag to select multiple slots.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, availability: {} }))}
                    className="text-xs text-red-400 hover:text-red-300 border border-red-800 hover:border-red-700 rounded-lg px-3 py-1.5 shrink-0 transition-colors"
                  >
                    Reset
                  </button>
                </div>

                <AvailabilityGrid
                  availability={formData.availability}
                  onChange={handleAvailabilityChange}
                  classType="tutor"
                />

                <div>
                  <FieldLabel>General Availability Notes</FieldLabel>
                  <textarea
                    name="availabilityNotes"
                    value={formData.availabilityNotes}
                    onChange={handleChange}
                    placeholder="e.g. Can do early mornings with advance notice, flexible during school holidays"
                    rows={2}
                    className={textareaCls}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Curriculum and Exams ──────────────────────────────────────────────────────── */}
          <div id="section-curriculum" className="bg-gray-900 rounded-xl border border-gray-800 shadow-sm overflow-hidden">
            <SectionHeader
              title="Curriculum and Exams"
              icon={<BookOpen size={18} />}
              isActive={activeSection === 'curriculum'}
              onClick={() => toggleSection('curriculum')}
            />
            {activeSection === 'curriculum' && (
              <div className="p-6 space-y-6">
                <p className="text-sm text-gray-400">Tick all that apply:</p>

                {CURRICULUMS.map((cat) => (
                  <div key={cat.category}>
                    <SubHeading>{cat.category}</SubHeading>
                    <div className="space-y-2">
                      {cat.options.map((opt) => {
                        const isChecked = formData.curriculums.includes(opt.value);
                        return (
                          <div key={opt.value}>
                            <CheckboxOption
                              label={opt.label}
                              checked={isChecked}
                              onChange={() => handleCheckbox('curriculums', opt.value)}
                            />
                            {isChecked && (
                              <textarea
                                value={formData.curriculumExperience[opt.value] || ''}
                                onChange={(e) => handleCurriculumExperienceChange(opt.value, e.target.value)}
                                placeholder="Briefly describe your experience with this curriculum and what outcomes students can expect…"
                                rows={3}
                                className={`${textareaCls} mt-2 ml-2`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <p className="text-sm font-bold text-purple-400">Professional &amp; Life Skills</p>

                {[
                  { key: 'schoolInterviewDetails', value: 'school_interview', heading: 'School Interview Coaching', placeholder: 'e.g. ESF, International schools, DSS, Local primary/secondary', helperText: 'Brief details about what you can coach:' },
                  { key: 'jobInterviewDetails', value: 'job_interview', heading: 'Job Interview Prep', placeholder: 'e.g. Banking and finance, Law, Tech, Hospitality, Retail', helperText: 'Brief details about industries or positions:' },
                  { key: 'businessMandarinDetails', value: 'business_mandarin', heading: 'Business Mandarin', placeholder: 'e.g. Business negotiations, Presentations, Email writing', helperText: 'Brief details about your areas of expertise:' },
                  { key: 'businessCantoneseDetails', value: 'business_cantonese', heading: 'Business Cantonese', placeholder: 'e.g. Client meetings, Phone calls, Industry-specific vocabulary', helperText: 'Brief details about your areas of expertise:' },
                ].map(({ key, value, heading, placeholder, helperText }) => {
                  const isChecked = formData.specialisms.includes(value);
                  return (
                    <div key={key}>
                      <CheckboxOption
                        label={heading}
                        checked={isChecked}
                        onChange={() => handleCheckbox('specialisms', value)}
                      />
                      {isChecked && (
                        <div className="mt-2 ml-2">
                          <p className="text-sm text-gray-400 mb-1.5">{helperText}</p>
                          <input
                            type="text"
                            name={key}
                            value={formData[key]}
                            onChange={handleChange}
                            placeholder={placeholder}
                            className={inputCls}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                <div>
                  <FieldLabel>Other curriculum or exam, please specify:</FieldLabel>
                  <input
                    type="text"
                    name="otherCurriculum"
                    value={formData.otherCurriculum}
                    onChange={handleChange}
                    placeholder="Enter any other curriculum you're familiar with"
                    className={inputCls}
                  />
                </div>

                <hr className="border-gray-800" />

                <div>
                  <FieldLabel>Textbooks you are familiar with:</FieldLabel>
                  <input
                    type="text"
                    name="textbooks"
                    value={formData.textbooks}
                    onChange={handleChange}
                    placeholder="List textbooks you've used"
                    className={inputCls}
                  />
                </div>

                <div>
                  <FieldLabel>Educational digital tools you're familiar with:</FieldLabel>
                  <input
                    type="text"
                    name="digitalTools"
                    value={formData.digitalTools}
                    onChange={handleChange}
                    placeholder="e.g. Quizlet, Anki"
                    className={inputCls}
                  />
                </div>

                <div>
                  <FieldLabel>Material Support</FieldLabel>
                  <div className="space-y-2">
                    {[
                      { value: 'manage_own', label: 'I am able to manage my own materials' },
                      { value: 'need_provided', label: 'I need to use your provided materials' },
                      { value: 'flexible', label: 'Both' },
                    ].map(({ value, label }) => (
                      <RadioOption
                        key={value}
                        label={label}
                        checked={formData.materialSupport === value}
                        onChange={() => handleRadio('materialSupport', value)}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <FieldLabel>
                    Are you capable of teaching according to the student's needs instead of just reading word by word from the textbook?
                  </FieldLabel>
                  <div className="space-y-2">
                    <RadioOption label="Yes" checked={formData.noTextbook === 'yes'} onChange={() => handleRadio('noTextbook', 'yes')} />
                    <RadioOption label="No" checked={formData.noTextbook === 'no'} onChange={() => handleRadio('noTextbook', 'no')} />
                  </div>
                </div>

                <div>
                  <FieldLabel>Can you build custom materials for students?</FieldLabel>
                  <div className="space-y-2">
                    <RadioOption label="Yes" checked={formData.customMaterials === 'yes'} onChange={() => handleRadio('customMaterials', 'yes')} />
                    <RadioOption label="No" checked={formData.customMaterials === 'no'} onChange={() => handleRadio('customMaterials', 'no')} />
                  </div>
                </div>

                {/* Sample lesson upload */}
                <div>
                  <FieldLabel>Sample Lesson (optional)</FieldLabel>
                  <label
                    htmlFor="upload-sample"
                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-700 rounded-xl p-6 text-center cursor-pointer hover:border-purple-500 transition-colors"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, 'sampleLesson')}
                  >
                    <input
                      id="upload-sample"
                      type="file"
                      accept=".pdf,.doc,.docx,.ppt,.pptx"
                      onChange={(e) => handleFileChange(e, 'sampleLesson')}
                      className="sr-only"
                    />
                    <Book size={36} className="text-gray-600 mb-3" />
                    {fileName.sampleLesson ? (
                      <>
                        <p className="text-green-400 font-medium flex items-center gap-1.5 text-sm">
                          <CheckCircle size={15} /> {fileName.sampleLesson}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">Click to change</p>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-300 text-sm">
                          <span className="text-purple-400 font-medium">Click to upload</span> a sample lesson
                        </p>
                        <p className="text-gray-500 text-xs mt-1">PDF, DOCX, PPTX (max 10 MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* ── Public Profile & Consent ──────────────────────────────────────────────── */}
          <div id="section-consent" className="bg-gray-900 rounded-xl border border-gray-800 shadow-sm overflow-hidden">
            <SectionHeader
              title="Public Profile and Consent"
              icon={<User size={18} />}
              isActive={activeSection === 'consent'}
              onClick={() => toggleSection('consent')}
            />
            {activeSection === 'consent' && (
              <div className="p-6">
                <FieldLabel required>May we show your tutor profile on our website?</FieldLabel>
                <div className="space-y-2 mt-2">
                  <RadioOption label="Yes, I consent" checked={formData.websiteConsent === 'yes'} onChange={() => handleRadio('websiteConsent', 'yes')} />
                  <RadioOption label="No, I do not consent" checked={formData.websiteConsent === 'no'} onChange={() => handleRadio('websiteConsent', 'no')} />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  If yes, your profile can appear so students can choose you.
                </p>
              </div>
            )}
          </div>

          {/* ── Compensation & Logistics ────────────────────────────────────────────────────── */}
          <div id="section-compensation" className="bg-gray-900 rounded-xl border border-gray-800 shadow-sm overflow-hidden">
            <SectionHeader
              title="Compensation and Logistics"
              icon={<DollarSign size={18} />}
              isActive={activeSection === 'compensation'}
              onClick={() => toggleSection('compensation')}
            />
            {activeSection === 'compensation' && (
              <div className="p-6 space-y-6">
                <p className="text-sm text-gray-400">
                  Your proposed hourly rates (HKD) — to help us match you correctly. Final rates
                  will be discussed after review.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>Online Rate (HKD/hour)</FieldLabel>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-3 flex items-center text-gray-500 text-sm pointer-events-none">
                        HKD
                      </span>
                      <input
                        type="number"
                        name="hourlyRateOnline"
                        value={formData.hourlyRateOnline}
                        onChange={handleChange}
                        min="0"
                        className={`${inputCls} pl-12`}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <FieldLabel required={formData.locationPreference !== 'online_only'}>
                      In-person Rate (HKD/hour)
                    </FieldLabel>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-3 flex items-center text-gray-500 text-sm pointer-events-none">
                        HKD
                      </span>
                      <input
                        type="number"
                        name="hourlyRateInPerson"
                        value={formData.hourlyRateInPerson}
                        onChange={handleChange}
                        min="0"
                        disabled={formData.locationPreference === 'online_only'}
                        className={`${inputCls} pl-12 disabled:opacity-40`}
                        required={formData.locationPreference !== 'online_only'}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <FieldLabel required>Lesson Location Preference</FieldLabel>
                  <div className="space-y-2">
                    <RadioOption label="Online only" checked={formData.locationPreference === 'online_only'} onChange={() => handleRadio('locationPreference', 'online_only')} />
                    <RadioOption label="In-person only" checked={formData.locationPreference === 'in_person_only'} onChange={() => handleRadio('locationPreference', 'in_person_only')} />
                    <RadioOption label="Both" checked={formData.locationPreference === 'both'} onChange={() => handleRadio('locationPreference', 'both')} />
                  </div>
                </div>

                {formData.locationPreference !== 'online_only' && (
                  <div>
                    <FieldLabel required>Locations You'll Travel to for In-person Lessons</FieldLabel>
                    <HongKongDistrictSelector
                      selectedDistricts={formData.baseDistricts}
                      onChange={handleDistrictsChange}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-center pt-2 pb-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-full text-white px-10 h-12 text-base font-semibold transition-colors disabled:opacity-60"
              style={{ backgroundColor: '#7e55f6' }}
              onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#6a46d1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#7e55f6'; }}
            >
              {isSubmitting ? 'Submitting…' : 'Submit Application'}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default ApplyPage;
